import { useCallback, useId, useRef, useState } from "react";
import GatewayIframe from "../components/mcp/GatewayIframe";
import LinkIcon from "../icons/LinkIcon";
import { onSpaLinkClick } from "../lib/link-click";
import { routeToHash, CONNECTIONS_TABS, type ConnectionsTab, type McpHelpSectionId } from "../lib/routing";
import { useGatewayFrame, type GatewayFrameState } from "../lib/use-gateway-frame";
import { manageDirectUrl } from "../lib/mcp-gateway";
import { useGatewaySignIn } from "../lib/use-gateway-sign-in";
import styles from "./MyConnectionsPage.module.css";

interface Props {
  initialTab?: ConnectionsTab;
  /** Called when the tab changes, so the URL can follow without adding history entries. */
  onTabChange: (tab: ConnectionsTab) => void;
  /** Opens the MCP Help topic, optionally on one section. */
  onHelp: (section?: McpHelpSectionId) => void;
}

const TAB_LABELS: Record<ConnectionsTab, string> = { credentials: "Credentials", consents: "Consents" };

/** localStorage key for the dismissed first-use note. */
export const FIRST_USE_NOTE_KEY = "my-connections:first-use-note:dismissed";

function readDismissed(): boolean {
  try {
    return localStorage.getItem(FIRST_USE_NOTE_KEY) === "1";
  } catch {
    return false;
  }
}

function errorMessage(reason: string): string {
  switch (reason) {
    case "action":
      return "That didn't go through, so the connection may still be active. Try again to see where it stands.";
    case "not-found":
    case "invalid-request":
      return "That connection wasn't found. It may already have been removed.";
    case "forbidden":
      return "You don't have access to that.";
    default:
      return "We couldn't load your connections.";
  }
}

/** One tab's framed list, with the states around it. */
function ConnectionsList({ tab }: { tab: ConnectionsTab }) {
  const frame = useGatewayFrame(tab);
  const signIn = useGatewaySignIn(frame.reload);
  const { state } = frame;
  const label = TAB_LABELS[tab].toLowerCase();
  const directUrl = manageDirectUrl(tab);

  return (
    <div>
      {state.phase === "loading" && (
        <p role="status" className={styles.status}>Loading your {label}…</p>
      )}

      {state.phase === "error" && <ErrorPanel state={state} onRetry={frame.reload} directUrl={directUrl} />}

      {state.phase === "no-response" && (
        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>Sign in to see your connections</h2>
          <p className={styles.panelText}>
            Your connections are kept by the MCP Auth Gateway, which needs its own sign-in. If you&rsquo;re already signed
            in, try again.{" "}
            {directUrl && (
              <a href={directUrl} target="_blank" rel="noopener noreferrer" className={styles.link}>
                Open it directly
              </a>
            )}
          </p>
          <div className={styles.actions}>
            <button type="button" onClick={signIn.open} className={styles.primaryButton}>Sign in</button>
            <button type="button" onClick={frame.reload} className={styles.secondaryButton}>Try again</button>
          </div>
          {signIn.blocked && (
            <p role="alert" className={styles.blocked}>
              Your browser blocked the sign-in window.{" "}
              {signIn.fallbackUrl && (
                <a href={signIn.fallbackUrl} target="_blank" rel="noopener noreferrer" className={styles.link}>
                  Open sign-in in a new tab
                </a>
              )}{" "}
              then come back; this page loads your connections again.
            </p>
          )}
        </div>
      )}

      <GatewayIframe frame={frame} title={`Your ${label}`} visible={state.phase === "ready"} />
    </div>
  );
}

function ErrorPanel({
  state,
  onRetry,
  directUrl,
}: {
  state: Extract<GatewayFrameState, { phase: "error" }>;
  onRetry: () => void;
  directUrl: string | null;
}) {
  return (
    <div role="alert" className={styles.panel}>
      <h2 className={styles.panelTitle}>{state.title}</h2>
      <p className={styles.panelText}>
        {errorMessage(state.reason)}{" "}
        {directUrl && (
          <a href={directUrl} target="_blank" rel="noopener noreferrer" className={styles.link}>
            Open it directly
          </a>
        )}
      </p>
      <div className={styles.actions}>
        <button type="button" onClick={onRetry} className={styles.primaryButton}>Try again</button>
      </div>
    </div>
  );
}

/**
 * My Connections (#939, Feature H): the gateway's Credentials and Consents pages, framed. What is
 * stored and who can see it is the gateway's; this page frames it, sizes it, and covers the
 * cases where it doesn't answer.
 */
export default function MyConnectionsPage({ initialTab = "credentials", onTabChange, onHelp }: Props) {
  const [tab, setTab] = useState<ConnectionsTab>(initialTab);
  const [noteDismissed, setNoteDismissed] = useState(readDismissed);
  const ids = useId();
  const tabRefs = useRef<Record<ConnectionsTab, HTMLButtonElement | null>>({ credentials: null, consents: null });

  const select = useCallback(
    (next: ConnectionsTab) => {
      setTab(next);
      onTabChange(next);
    },
    [onTabChange],
  );

  const onTabKeyDown = (event: React.KeyboardEvent, current: ConnectionsTab) => {
    const index = CONNECTIONS_TABS.indexOf(current);
    let next: ConnectionsTab | undefined;
    if (event.key === "ArrowRight") next = CONNECTIONS_TABS[(index + 1) % CONNECTIONS_TABS.length];
    else if (event.key === "ArrowLeft") next = CONNECTIONS_TABS[(index + CONNECTIONS_TABS.length - 1) % CONNECTIONS_TABS.length];
    else if (event.key === "Home") next = CONNECTIONS_TABS[0];
    else if (event.key === "End") next = CONNECTIONS_TABS[CONNECTIONS_TABS.length - 1];
    if (!next) return;
    event.preventDefault();
    select(next);
    tabRefs.current[next]?.focus();
  };

  const dismissNote = () => {
    setNoteDismissed(true);
    try {
      localStorage.setItem(FIRST_USE_NOTE_KEY, "1");
    } catch {
      // Storage unavailable: the note is dismissed for this visit only.
    }
  };

  return (
    <div>
      <div className={styles.titleBlock}>
        <h1 className={styles.title}>My Connections</h1>
        <p className={styles.subtitle}>
          What the MCP Auth Gateway holds on your behalf for MCP servers. Revoke anything you no longer use; revoking and
          reconnecting also fixes most stale-credential sign-in errors. Only you can see this.
        </p>
      </div>

      {!noteDismissed && (
        <div className={styles.note}>
          <LinkIcon className={styles.noteIcon} />
          <div className={styles.noteBody}>
            <strong>Where connections show up.</strong> After you connect a server, the platform keeps two things for you.{" "}
            <strong>Credentials</strong> are the sign-ins it stores so you don&rsquo;t repeat them, and{" "}
            <strong>Consents</strong> are the access you&rsquo;ve allowed apps like Claude. Revoke either to disconnect; you
            can connect again from the server&rsquo;s page.{" "}
            <a
              href={routeToHash({ page: "help", section: "mcp-managing" })}
              onClick={onSpaLinkClick(() => onHelp("mcp-managing"))}
              className={styles.link}
            >
              More in Help →
            </a>
          </div>
          <button type="button" onClick={dismissNote} className={styles.dismiss}>Got it</button>
        </div>
      )}

      <div role="tablist" aria-label="My Connections" className={styles.tabs}>
        {CONNECTIONS_TABS.map((id) => (
          <button
            key={id}
            ref={(el) => { tabRefs.current[id] = el; }}
            type="button"
            role="tab"
            id={`${ids}-tab-${id}`}
            aria-selected={tab === id}
            aria-controls={`${ids}-panel-${id}`}
            tabIndex={tab === id ? 0 : -1}
            onClick={() => select(id)}
            onKeyDown={(e) => onTabKeyDown(e, id)}
            className={tab === id ? `${styles.tab} ${styles.tabActive}` : styles.tab}
          >
            {TAB_LABELS[id]}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={`${ids}-panel-${tab}`} aria-labelledby={`${ids}-tab-${tab}`} className={styles.panelArea}>
        <ConnectionsList key={tab} tab={tab} />
      </div>
    </div>
  );
}
